import type { Calendar, EffectivePriorityCtx, Priority, Task } from "@app/core";
import { effectivePriorityOf, effectiveUrgencyOf } from "@app/core";
import type { LabelMatchMode } from "../stores/labelFilter.store.js";
import { visibleTasks } from "./calendar-visibility.js";
import { filterByLabels } from "./label-filter.js";

/** Day-anchored time + settings context for the deadline-derived urgency/priority
 *  filters (stable per day so the memo/cache doesn't thrash). Same shape the
 *  sorts and block-color resolver use — see `@app/core` `EffectivePriorityCtx`. */
export type MatrixFilterCtx = EffectivePriorityCtx;

/**
 * Filter tasks by selected topic ids. Mirrors {@link filterByLabels}: topics are
 * many-per-task (`Task.topic_ids`), empty selection = no filter, "any" keeps
 * tasks carrying at least one selected topic, "all" keeps only tasks carrying
 * every selected topic.
 */
export function filterByTopics(
  tasks: Task[],
  selected: string[],
  mode: LabelMatchMode,
): Task[] {
  if (selected.length === 0) return tasks;
  const selectedSet = new Set(selected);
  return tasks.filter((t) => {
    return mode === "all"
      ? selected.every((s) => t.topic_ids.includes(s))
      : t.topic_ids.some((id) => selectedSet.has(id));
  });
}

/**
 * Filter tasks by priority — the LIVE priority (matrix diagonal, deadline-derived
 * when in scope; else the stored flag), so it matches what the blocks show. A
 * task carries exactly one priority, so this is a plain membership test. Empty
 * selection = no filter.
 */
export function filterByPriorities(tasks: Task[], selected: Priority[], ctx: MatrixFilterCtx): Task[] {
  if (selected.length === 0) return tasks;
  const set = new Set(selected);
  return tasks.filter((t) => set.has(effectivePriorityOf(t, ctx)));
}

/**
 * Filter tasks by urgency — the horizontal third of `matrix_pos`, or, for a task
 * never placed / flagged auto, the level DERIVED from its deadline (`deadline_at`
 * falling back to `due_at`) when the config is enabled ("none" otherwise).
 * Mirrors {@link filterByPriorities}: exactly one urgency level, empty = no filter.
 */
export function filterByUrgencies(tasks: Task[], selected: Priority[], ctx: MatrixFilterCtx): Task[] {
  if (selected.length === 0) return tasks;
  const set = new Set(selected);
  return tasks.filter((t) => set.has(effectiveUrgencyOf(t, ctx)));
}

export interface ViewFilterInputs {
  calendars: Calendar[];
  label: { selected: string[]; mode: LabelMatchMode };
  topic: { selected: string[]; mode: LabelMatchMode };
  priority: { selected: Priority[] };
  urgency: { selected: Priority[] };
  /** Shared time/settings context for the deadline-derived urgency + priority. */
  matrix: MatrixFilterCtx;
}

/**
 * The single source of truth for "which tasks does the current sidebar state
 * show". Chains calendar-visibility → label → topic → priority → urgency. Pure
 * (no store reads) so it's unit-testable; the `useViewFilters` hook binds it to
 * the stores. Applied at every `expandTasksForCalendar` call site + the
 * All-tasks list + the inbox.
 */
export function applyViewFilters(tasks: Task[], f: ViewFilterInputs): Task[] {
  let out = visibleTasks(tasks, f.calendars);
  out = filterByLabels(out, f.label.selected, f.label.mode);
  out = filterByTopics(out, f.topic.selected, f.topic.mode);
  out = filterByPriorities(out, f.priority.selected, f.matrix);
  out = filterByUrgencies(out, f.urgency.selected, f.matrix);
  return out;
}
